/**
 * YouTube 下载 API 服务
 */

const youtubeAPI = {
  /**
   * 提交YouTube视频下载任务
   * @param {string} url - YouTube视频链接
   * @returns {Promise<{success: boolean, job_id: string, status: string, message: string}>}
   */
  submitDownload: async (url) => {
    try {
      const response = await fetch('/testapi/youtube/download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('提交YouTube下载任务失败:', error);
      throw new Error('网络错误，请稍后重试');
    }
  },

  /**
   * 查询下载任务状态
   * @param {string} jobId - 任务ID
   * @returns {Promise<{success: boolean, job_id: string, status: string, outputs?: Array, download_url?: string}>}
   */
  getJobStatus: async (jobId) => {
    try {
      const response = await fetch(`/testapi/youtube/status/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('查询任务状态失败:', error);
      throw new Error('网络错误，请稍后重试');
    }
  },

  /**
   * 轮询任务状态直到完成
   * @param {string} jobId - 任务ID
   * @param {Function} onProgress - 进度回调函数
   * @param {number} maxWaitTime - 最大等待时间（毫秒），默认5分钟
   * @returns {Promise<{success: boolean, job_id: string, status: string, outputs?: Array, download_url?: string}>}
   */
  pollJobStatus: async (jobId, onProgress = null, maxWaitTime = 5 * 60 * 1000) => {
    const startTime = Date.now();
    const pollInterval = 2000; // 2秒轮询一次

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          // 检查是否超时
          if (Date.now() - startTime > maxWaitTime) {
            reject(new Error('任务处理超时，请稍后重试'));
            return;
          }

          const result = await youtubeAPI.getJobStatus(jobId);
          
          // 调用进度回调
          if (onProgress) {
            onProgress(result);
          }

          // 检查任务状态
          if (result.status === 'finished') {
            resolve(result);
          } else if (result.status === 'failed') {
            reject(new Error('任务处理失败'));
          } else {
            // 继续轮询
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          reject(error);
        }
      };

      // 开始轮询
      poll();
    });
  },

  /**
   * 验证YouTube URL格式
   * @param {string} url - 要验证的URL
   * @returns {boolean} 是否为有效的YouTube URL
   */
  validateYouTubeUrl: (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/).+/;
    return youtubeRegex.test(url);
  },

  /**
   * 从API响应中提取下载URL
   * @param {Object} result - API响应结果
   * @returns {string|null} 下载URL或null
   */
  extractDownloadUrl: (result) => {
    if (result.outputs && result.outputs.length > 0) {
      const output = result.outputs[0];
      if (output.data && output.data.url) {
        return output.data.url;
      }
    }
    return result.download_url || null;
  },

  /**
   * 使用 Lemonfox AI 进行音频转录
   * @param {string} videoUrl - 视频文件URL
   * @param {string} language - 语言设置，默认为 "chinese"
   * @param {string} responseFormat - 响应格式，默认为 "json"
   * @returns {Promise<Object>} 转录结果
   */
  transcribeWithLemonfox: async (videoUrl, language = "chinese", responseFormat = "json") => {
    try {
      // 创建 FormData，按照curl格式
      const formData = new FormData();
      formData.append('file', videoUrl);  // 直接传递视频文件URL
      formData.append('language', language);
      formData.append('response_format', responseFormat);

      console.log('开始调用 Lemonfox AI 转录API...');
      console.log('视频URL:', videoUrl);
      console.log('语言设置:', language);
      console.log('输出格式:', responseFormat);

      const response = await fetch('https://api.lemonfox.ai/v1/audio/transcriptions', {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer LEu1kRBnqMwhChNQvd2Mw2sqEOLqrf5A' // 使用提供的API KEY
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API响应错误:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, response: ${errorText}`);
      }

      // 根据响应格式处理数据
      let data;
      if (responseFormat === 'json' || responseFormat === 'verbose_json') {
        data = await response.json();
      } else {
        // text、srt、vtt 格式返回文本
        data = await response.text();
      }
      
      // 打印返回结果
      console.log('=== Lemonfox AI 转录结果 ===');
      console.log('语言:', language);
      console.log('格式:', responseFormat);
      if (typeof data === 'string') {
        console.log('结果:', data);
      } else {
        console.log(JSON.stringify(data, null, 2));
      }
      console.log('========================');
      
      return data;
    } catch (error) {
      console.error('Lemonfox AI 转录失败:', error);
      throw new Error('转录失败: ' + error.message);
    }
  },

  /**
   * 获取支持的语言列表
   * @returns {Array} 语言选项数组
   */
  getSupportedLanguages: () => {
    return [
      { code: 'chinese', name: '中文' },
      { code: 'english', name: '英语' },
      { code: 'japanese', name: '日语' },
      { code: 'korean', name: '韩语' },
      { code: 'spanish', name: '西班牙语' },
      { code: 'french', name: '法语' },
      { code: 'german', name: '德语' },
      { code: 'italian', name: '意大利语' },
      { code: 'portuguese', name: '葡萄牙语' },
      { code: 'russian', name: '俄语' },
      { code: 'arabic', name: '阿拉伯语' },
      { code: 'hindi', name: '印地语' },
      { code: 'thai', name: '泰语' },
      { code: 'vietnamese', name: '越南语' },
      { code: 'auto', name: '自动检测' }
    ];
  },

  /**
   * 获取支持的输出格式列表
   * @returns {Array} 格式选项数组
   */
  getSupportedFormats: () => {
    return [
      { code: 'json', name: 'JSON', description: '结构化JSON格式，包含文本和时间戳' },
      { code: 'text', name: 'TEXT', description: '纯文本格式，只包含转录文本' },
      { code: 'srt', name: 'SRT', description: '字幕文件格式，包含时间戳' },
      { code: 'verbose_json', name: 'Verbose JSON', description: '详细JSON格式，包含更多元数据' },
      { code: 'vtt', name: 'VTT', description: 'WebVTT字幕格式' }
    ];
  },

  /**
   * 查询转文本任务状态
   * @param {string} jobId - 任务ID
   * @returns {Promise<{success: boolean, job_id: string, status: string, transcription?: Array}>}
   */
  getTranscriptionStatus: async (jobId) => {
    try {
      // 使用新的转录状态查询接口（假设存在）
      const response = await fetch(`/zd_speech/zd_speech_file_controller/get_transcription_status/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('查询转文本状态失败:', error);
      throw new Error('网络错误，请稍后重试');
    }
  },

  /**
   * 轮询转文本任务状态直到完成
   * @param {string} jobId - 任务ID
   * @param {Function} onProgress - 进度回调函数
   * @param {number} maxWaitTime - 最大等待时间（毫秒），默认10分钟
   * @returns {Promise<{success: boolean, job_id: string, status: string, transcription?: Array}>}
   */
  pollTranscriptionStatus: async (jobId, onProgress = null, maxWaitTime = 10 * 60 * 1000) => {
    const startTime = Date.now();
    const pollInterval = 3000; // 3秒轮询一次，转文本可能需要更长时间

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          // 检查是否超时
          if (Date.now() - startTime > maxWaitTime) {
            reject(new Error('转文本任务处理超时，请稍后重试'));
            return;
          }

          const result = await youtubeAPI.getTranscriptionStatus(jobId);
          
          // 调用进度回调
          if (onProgress) {
            onProgress(result);
          }

          // 检查任务状态
          if (result.status === 'finished') {
            resolve(result);
          } else if (result.status === 'failed') {
            reject(new Error('转文本任务处理失败'));
          } else {
            // 继续轮询
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          reject(error);
        }
      };

      // 开始轮询
      poll();
    });
  },

  /**
   * 从转文本API响应中提取文本片段
   * @param {Object} result - API响应结果
   * @returns {Array|null} 文本片段数组或null
   */
  extractTranscriptionSegments: (result) => {
    if (result.transcription && Array.isArray(result.transcription)) {
      return result.transcription.map((segment, index) => ({
        id: index + 1,
        startTime: segment.start_time || 0,
        endTime: segment.end_time || 0,
        text: segment.text || '',
        isModified: false
      }));
    }
    return null;
  },

};

export default youtubeAPI;